/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import { useEffect, useRef, useState } from 'react';

const TICK_MS = 1000;

/**
 * Counts `waitMs` down to zero, calling `onElapsed` once when it gets there.
 *
 * Pass `null` to stay inactive, which is also how a caller cancels: the interval
 * is torn down and `onElapsed` never fires. Passing a different `waitMs` restarts
 * the countdown from the new value.
 *
 * @returns the remaining milliseconds, or null while inactive
 */
export function useCountdown(
    waitMs: number | null,
    onElapsed: () => void
): number | null {
    const [countdown, setCountdown] = useState({ waitMs, remainingMs: waitMs });

    // Reconciled during render rather than in an effect, so the first paint after
    // the countdown starts or restarts already shows the new value
    if (countdown.waitMs !== waitMs) {
        setCountdown({ waitMs, remainingMs: waitMs });
    }

    // Held in a ref so an inline callback does not restart the countdown on every
    // render
    const onElapsedRef = useRef(onElapsed);
    onElapsedRef.current = onElapsed;

    useEffect(() => {
        if (waitMs === null) {
            return;
        }

        // A fixed deadline rather than a counter decremented per tick: a throttled
        // background tab fires this interval late, and recomputing means a late
        // tick lands on the right value instead of accumulating the difference
        const deadline = Date.now() + waitMs;

        const intervalId = setInterval(() => {
            const remainingMs = Math.max(deadline - Date.now(), 0);
            setCountdown({ waitMs, remainingMs });

            if (remainingMs === 0) {
                clearInterval(intervalId);
                onElapsedRef.current();
            }
        }, TICK_MS);

        return () => clearInterval(intervalId);
    }, [waitMs]);

    return countdown.remainingMs;
}
